import React, { useState, useContext } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Formik } from 'formik';
import * as Yup from 'yup';

const Login = () => {
  const { login, error, isLoading } = useContext(AuthContext);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  // Initial form values
  const initialValues = {
    email: '',
    password: ''
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoginError('');
    try {
      const success = await login(values.email, values.password);
      if (!success) {
        setLoginError('Invalid email or password');
      }
    } catch (err) {
      setLoginError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-fade-in" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={5} lg={4}>
            <div className="text-center mb-4">
              <div style={{
                width: 56, height: 56, borderRadius: '16px', margin: '0 auto 12px',
                background: 'linear-gradient(135deg, #7C6FA0, #9B8FBF)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(124,111,160,0.25)'
              }}>
                <span style={{ fontSize: '1.5rem', color: '#fff' }}>&#128203;</span>
              </div>
              <h3 style={{ fontWeight: 700, color: '#2D2D2D', marginBottom: 4 }}>Welcome back</h3>
              <p style={{ color: '#9E9E9E', fontSize: '0.9rem' }}>Sign in to your account</p>
            </div>

            <Card style={{ border: '1px solid #EEEEEE', borderRadius: 16, boxShadow: '0 4px 20px rgba(124,111,160,0.08)' }}>
              <Card.Body style={{ padding: '2rem' }}>
                {(loginError || error) && (
                  <Alert variant="danger" style={{ borderRadius: 10, fontSize: '0.88rem' }}>{loginError || error}</Alert>
                )}
                
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.email && errors.email}
                          placeholder="Enter your email"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Password</Form.Label>
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && errors.password}
                            placeholder="Enter your password"
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <Button
                            variant="link"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              padding: '4px',
                              color: '#9E9E9E',
                              textDecoration: 'none',
                              border: 'none',
                              background: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#7C6FA0'}
                            onMouseLeave={(e) => e.target.style.color = '#9E9E9E'}
                          >
                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                          </Button>
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div className="d-grid gap-2">
                        <Button 
                          variant="primary" 
                          type="submit" 
                          disabled={isSubmitting || isLoading}
                          style={{ padding: '0.65rem', fontWeight: 600, borderRadius: 10 }}
                        >
                          {isLoading ? 'Signing in...' : 'Sign In'}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
                
                <div className="text-center mt-3">
                  <p style={{ color: '#9E9E9E', fontSize: '0.85rem', marginBottom: 0 }}>
                    Don't have an account? <Link to="/register" style={{ color: '#7C6FA0', fontWeight: 600, textDecoration: 'none' }}>Go Back</Link>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
